<template>
  <Layout>
    <b-row class="pb-4 pt-2">
      <b-col class="d-flex justify-content-between">
        <Button location="title" type="white" @click="handleCancelPurchase()">Voltar</Button>
        <h1 class="title">Nova Compra</h1>
        <ImportXmlFile
          :disabled="importedInvoiceFormData !== null"
          @file="handleXmlFile"
          @manifest="handleManifestedXmlModal"
        />
      </b-col>
    </b-row>
    <TaxInformation ref="taxInformation" />
    <LinkedDocuments ref="linkedDocuments" v-if="showLinkedDocuments" />
    <Supplier ref="supplier" />
    <Products ref="products" />
    <TaxCalc ref="taxCalc" />
    <Transport ref="transport" />
    <Payment ref="payments" />
    <ModalFiscalProduct />
    <ModalPriceUpdate :confirm="onConfirmPriceUpdateModal" v-if="modalPriceUpdateIsOpen" />
    <ModalImportXml :confirm="confirm" v-if="modalImportXmlIsOpen" />
    <ModalManifestedXmls :confirm="importManifestToPurchase" v-if="modalManifestedXmlsIsOpen" />
    <b-row class="margin-button-save text-center">
      <b-col>
        <Button type="cancel" @click="handleCancelPurchase()">
          <Close class="mb-1" title="Cancelar" /> Cancelar
        </Button>
        <Button type="success" @click="handleSavePurchase()">Salvar</Button>
      </b-col>
    </b-row>
  </Layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Close from 'vue-material-design-icons/Close';
import { Layout, ImportXmlFile } from '@/components';
import { Button } from '@/components/Form';
import FormatMixin from '@/modules/Application/mixins/FormatMixin';
import TaxInformation from '../components/Collapses/TaxInformation/TaxInformation';
import LinkedDocuments from '../components/Collapses/LinkedDocuments/LinkedDocuments';
import Supplier from '../components/Collapses/Supplier';
import Products from '../components/Collapses/Products/Products';
import TaxCalc from '../components/Collapses/TaxCalc';
import Payment from '../components/Collapses/Payment';
import Transport from '../components/Collapses/Transport';
import ModalFiscalProduct from '../components/Modals/ModalFiscalProduct/ModalFiscalProduct';
import ModalImportXml from '../components/Modals/ModalImportXml/ModalImportXml';
import ModalManifestedXmls from '../components/Modals/ModalManifestedXmls/ModalManifestedXmls';
import ModalPriceUpdate from '../components/Modals/ModalPriceUpdate/ModalPriceUpdate';
import { MODELO } from '../../../../Fiscal/enums/Documento';

export default {
  name: 'purchaseRegister',
  mixins: [FormatMixin],
  components: {
    Layout,
    Button,
    TaxInformation,
    LinkedDocuments,
    Supplier,
    Products,
    TaxCalc,
    Payment,
    Transport,
    ModalFiscalProduct,
    ImportXmlFile,
    ModalImportXml,
    ModalManifestedXmls,
    ModalPriceUpdate,
    Close,
  },
  data() {
    return {
      collapsesToValidate: ['taxInformation', 'supplier', 'products', 'payments'],
    };
  },
  computed: {
    ...mapState('purchase', ['purchase', 'importedInvoice', 'importedInvoiceFormData']),
    ...mapState('componentsPurchase', [
      'modalImportXmlIsOpen',
      'modalManifestedXmlsIsOpen',
      'modalPriceUpdateIsOpen',
    ]),
    ...mapState('client', ['client']),
    showLinkedDocuments() {
      const cfop = this.purchase?.taxInformation?.natureza?.cfop?.codigo;
      const cfopWithoutOperation = cfop?.substr(1, 3);
      if (!cfopWithoutOperation) return false;

      const cfopsWatched = ['351', '352', '353'];

      return (
        this.purchase.taxInformation.nfeModel === MODELO.CTE &&
        cfopsWatched.includes(cfopWithoutOperation)
      );
    },
  },
  props: {
    confirm: {
      type: Function,
    },
    manifestoId: {
      required: false,
      type: Number,
    },
  },
  created() {
    this.setDecimalConfiguration();
  },
  async mounted() {
    if (this.emptyIssuerBasicData()) {
      this.$router.push({ path: '/inventory/purchase' });
      return;
    }

    await Promise.all([
      this.setOrigemCollection(),
      this.setCSTCollection(),
      this.setCSTIPICollection(),
      this.setCSTPISCollection(),
      this.setCSTCOFINSCollection(),
      this.setCENQCollection(),
      this.setCSOSNCollection(),
      this.setUnitOfMeasureCollection(),
    ]);

    if (this.manifestoId) {
      try {
        this.setLoading(true);
        await this.importManifestToPurchase({ id: this.manifestoId });
      } finally {
        this.setLoading(false);
      }
    }
  },
  methods: {
    ...mapActions('cenq', ['setCENQCollection']),
    ...mapActions('csosn', ['setCSOSNCollection']),
    ...mapActions('client', ['setClient']),
    ...mapActions('origem', ['setOrigemCollection']),
    ...mapActions('application', ['setLoading']),
    ...mapActions('unitOfMeasure', ['setUnitOfMeasureCollection']),
    ...mapActions('purchase', [
      'importXml',
      'setPurchase',
      'savePurchase',
      'updatePurchaseData',
      'hasInvalidCollapse',
      'setDecimalConfiguration',
      'importManifestoIntoPurchase',
    ]),
    ...mapActions('cst', [
      'setCSTCollection',
      'setCSTIPICollection',
      'setCSTPISCollection',
      'setCSTCOFINSCollection',
    ]),
    ...mapActions('componentsPurchase', [
      'toggleModalImportXml',
      'toggleModalManifestedXmls',
      'toggleModalPriceUpdate',
    ]),
    emptyIssuerBasicData() {
      return !this.client.addressCollection.length || !this.client.identification;
    },
    setDefaultFields() {
      this.$refs.payments.setDefaultFields();
    },
    async isValidPurchase() {
      this.$refs.taxInformation.$v.$touch();
      this.$refs.supplier.$v.$touch();
      this.$refs.products.$v.$touch();
      this.$refs.payments.$v.$touch();

      const params = {
        collapsesToValidate: this.collapsesToValidate,
        refs: this.$refs,
        root: this.$root,
      };

      if (await this.hasInvalidCollapse(params)) return false;
      return true;
    },
    async handleSavePurchase() {
      this.setDefaultFields();
      if (!(await this.isValidPurchase())) return false;

      await this.setPurchase();

      if (this.entryDateIsLowerThanIssueDate()) {
        return this.$alert.warning('Data de entrada não pode ser menor que data de emissão.');
      }

      this.setEmptyProductOfPurchaseCfops();

      if (!this.validatePayment()) {
        return this.confirm({
          title: 'Confirmação',
          message: 'As formas de pagamento não correspondem com o total da nota, deseja continuar?',
          onOk: () => this.registerPurchase(),
        });
      }

      return this.registerPurchase();
    },
    setEmptyProductOfPurchaseCfops() {
      const { taxInformation, products } = this.purchase;
      const { cfop } = taxInformation.natureza;

      products.productOfPurchaseCollection = products.productOfPurchaseCollection.map((e) => {
        const productCfopId = e.fiscal.ids.cfopId;

        if (productCfopId) return e;

        e.fiscal.values.cfop = cfop;
        e.fiscal.ids.cfopId = cfop.id;

        return e;
      });
    },
    setPurchaseCfopInItems() {
      const { taxInformation, products } = this.purchase;
      const { cfop } = taxInformation.natureza;

      products.productOfPurchaseCollection = products.productOfPurchaseCollection.map((e) => {
        e.fiscal.values.cfop = cfop;
        e.fiscal.ids.cfopId = cfop.id;

        return e;
      });
    },
    async onConfirmPriceUpdateModal(productOfPurchaseCollection) {
      await this.updatePurchaseData({
        ...this.purchase,
        products: {
          productOfPurchaseCollection,
        },
      });

      this.toggleModalPriceUpdate();
    },
    async registerPurchase() {
      this.setLoading(true);

      try {
        await this.savePurchase(this.purchase);
      } finally {
        this.setLoading(false);
        const naturezaId = this.purchase.taxInformation.natureza.id;
        localStorage.setItem('lastPurchaseNaturezaId', naturezaId);
      }
      this.$alert.success('Compra salva com sucesso!');
      return this.$router.push({ path: '/inventory/purchase' });
    },
    validatePayment() {
      const NONE_PAYMENT = 'Nenhum';
      const purchasePaymentName = this.purchase.payments.paymentModeCollection[0].name;
      const totalNF = this.purchase.taxCalc.vNF;
      const totalPayment = this.purchase.payments.paymentModeCollection.reduce(
        (acc, item) => acc + this.round(item.value, 2),
        0,
      );

      if (purchasePaymentName !== NONE_PAYMENT && totalPayment !== totalNF) {
        return false;
      }

      return true;
    },
    entryDateIsLowerThanIssueDate() {
      const entryDate = this.$moment(this.purchase.taxInformation.entryDate).format('YYYY-MM-DD');
      const issueDate = this.$moment(this.purchase.taxInformation.issueDate).format('YYYY-MM-DD');
      return entryDate < issueDate;
    },
    handleCancelPurchase() {
      return this.$router.push({ name: 'Purchase' });
    },
    async importManifestToPurchase(manifest) {
      if (!manifest || !manifest.id) return;
      await this.importManifestoIntoPurchase(manifest);
    },
    async handleXmlFile(file) {
      this.setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        await this.importXml(formData);
      } finally {
        this.setLoading(false);
      }
    },
    async handleManifestedXmlModal() {
      this.toggleModalManifestedXmls();
    },
  },
  watch: {
    importedInvoice(value) {
      if (!value && this.modalImportXmlIsOpen) return this.toggleModalImportXml();

      if (value) {
        const { warnings } = this.importedInvoice;
        if (warnings) {
          warnings.forEach((i) => {
            this.$alert.warning(i);
          });
        }

        if (this.modalManifestedXmlsIsOpen) {
          this.toggleModalManifestedXmls();
        }

        if (!this.modalImportXmlIsOpen) {
          this.toggleModalImportXml();
        }
      }

      return null;
    },
    modalImportXmlIsOpen(isOpen) {
      if (isOpen) return;

      if (!this.importedInvoiceFormData) {
        return;
      }

      if (!this.client.isRequestPriceUpdateEnabled) {
        return;
      }

      this.toggleModalPriceUpdate();
    },
  },
};
</script>
